// CAPE/CIN segmentation + LFC/EL detection.
// Algorithm ported from docs/skysight-skewt-bundle.js.

/**
 * Linearly interpolate env temp at a given pressure.
 * Assumes envData is sorted by descending pressure (surface first, top last).
 */
function interpEnvT(envData, p) {
  for (let i = 0; i < envData.length - 1; i++) {
    const a = envData[i];
    const b = envData[i + 1];
    if (p <= a.press && p >= b.press) {
      const f = (a.press - p) / (a.press - b.press);
      return a.temp + f * (b.temp - a.temp);
    }
  }
  return null;
}

/**
 * Walk parcelPath; for each level classify CAPE (parcel > env) or CIN, build segments.
 */
export function segmentCAPE_CIN(parcelPath, envData, basep) {
  const segments = [];
  let current = null;
  let prevType = null;

  for (const pt of parcelPath) {
    if (pt.p > basep - 10) continue;
    const envT = interpEnvT(envData, pt.p);
    if (envT === null) continue;
    const type = (pt.t > envT) ? 'CAPE' : 'CIN';

    if (type !== prevType) {
      if (current) {
        const cross = { t: envT, p: pt.p };
        current.parcelPath.push(cross);
        current.envPath.push(cross);
        segments.push(current);
      }
      current = { type, parcelPath: [], envPath: [] };
      const cross = { t: envT, p: pt.p };
      current.parcelPath.push(cross);
      current.envPath.push(cross);
    }
    current.parcelPath.push(pt);
    current.envPath.push({ t: envT, p: pt.p });
    prevType = type;
  }
  if (current) segments.push(current);
  return segments;
}

/**
 * First level where parcel.t > env.t (sign change CIN→CAPE).
 * @returns {{pressure, temperature}|null}
 */
export function findLFC(parcelPath, envData) {
  let prevSign = null;
  for (const pt of parcelPath) {
    const envT = interpEnvT(envData, pt.p);
    if (envT === null) continue;
    const sign = pt.t > envT ? 1 : -1;
    if (prevSign === -1 && sign === 1) {
      return { pressure: pt.p, temperature: envT };
    }
    prevSign = sign;
  }
  return null;
}

/**
 * Skysight-style LFC. Only considers parcel points ABOVE LCL (and below
 * surface - 20 hPa). Returns the first pressure where the parcel is warmer
 * than the environment in that range. This is more lenient than findLFC for
 * convective cases where the parcel is buoyant immediately above LCL, but
 * STRICTER for marginal cases (where parcel never overcomes env above LCL)
 * — exactly matching when Skysight chooses to show "Equilibrium Level".
 *
 * Source: docs/skysight-skewt-bundle.js
 *   calculateLFCPressure(parcelPath, surfacePress) — walks parcel skipping
 *   points where a.p >= plcl or a.p >= surfacePress - 20, then returns the
 *   first a.p where parcel.t > env.t (interpolated).
 *
 * @param {Array<{t,p}>} parcelPath - lifted parcel ascent
 * @param {Array<{press,temp}>} envData - sounding (descending pressure)
 * @param {number} surfacePress - surface pressure in hPa
 * @param {number} plcl - LCL pressure in hPa
 * @returns {number|null} LFC pressure in hPa, or null
 */
export function calculateLFCPressure(parcelPath, envData, surfacePress, plcl) {
  if (!Number.isFinite(plcl) || !Number.isFinite(surfacePress)) return null;
  for (const pt of parcelPath) {
    if (pt.p >= plcl || pt.p >= surfacePress - 20) continue;
    const envT = interpEnvT(envData, pt.p);
    if (envT === null) continue;
    if (pt.t > envT) return pt.p;
  }
  return null;
}

/**
 * Compute CIN (J/kg) by integrating negative buoyancy over CIN segments
 * BELOW the LFC. Above the LFC the parcel is, by definition, positively
 * buoyant overall — any small env crossings up there are not "convective
 * inhibition" in the classical sense and shouldn't add to the value used
 * to gate cumulus-tower visualization.
 *
 *   CIN = R_d * Σ_segments_below_LFC Σ_steps (T_env_K - T_parcel_K) * ln(p_lower/p_upper)
 * Returns a POSITIVE value (magnitude of inhibition). If no LFC is supplied,
 * every CIN segment is counted (legacy behavior).
 *
 * @param {Array<{type, parcelPath, envPath}>} segments
 * @param {{pressure: number}|null} [lfc]
 * @returns {number} CIN in J/kg (positive)
 */
export function computeCIN(segments, lfc) {
  const R_d = 287; // J/(kg·K)
  const lfcP = lfc && Number.isFinite(lfc.pressure) ? lfc.pressure : null;
  let cin = 0;
  for (const seg of segments) {
    if (seg.type !== 'CIN') continue;
    const pPath = seg.parcelPath;
    const ePath = seg.envPath;
    const n = Math.min(pPath.length, ePath.length);
    for (let i = 1; i < n; i++) {
      const pA = pPath[i - 1].p;
      const pB = pPath[i].p;
      if (pA <= 0 || pB <= 0 || pA === pB) continue;
      // Skip if BOTH endpoints are above LFC (lower pressure than LFC).
      if (lfcP !== null && pA < lfcP && pB < lfcP) continue;
      const tParA = pPath[i - 1].t + 273.15;
      const tParB = pPath[i].t + 273.15;
      const tEnvA = ePath[i - 1].t + 273.15;
      const tEnvB = ePath[i].t + 273.15;
      const dT = ((tEnvA - tParA) + (tEnvB - tParB)) / 2;
      cin += R_d * dT * Math.log(Math.max(pA, pB) / Math.min(pA, pB));
    }
  }
  return cin;
}

/**
 * Equilibrium Level — top of the highest CAPE segment that's at least
 * 50 hPa thick. If `lfc` is provided we require LFC.pressure - top.p >= 50.
 * If `lfc` is null (parcel was warmer than env from the start, common in
 * convective scenarios), we instead require the CAPE segment itself to span
 * at least 50 hPa.
 *
 * @returns {{pressure, temperature}|null}
 */
export function findEL(segments, lfc) {
  let best = null;
  for (const seg of segments) {
    if (seg.type !== 'CAPE' || seg.parcelPath.length === 0) continue;
    const bot = seg.parcelPath[0];
    const top = seg.parcelPath[seg.parcelPath.length - 1];
    const thickness = lfc
      ? lfc.pressure - top.p
      : bot.p - top.p;
    if (thickness < 50) continue;
    if (best === null || top.p < best.pressure) {
      best = { pressure: top.p, temperature: top.t };
    }
  }
  return best;
}
